<template>
  <modal
    v-if="show"
    height="650px"
    width="609px"
    bodyOverflow="auto"
    :title="t('Integrate Account')"
    @close="close"
    :read-article-url="`https://docs.bemob.com/${locale}/facebook-settings`"
  >
    <div class="w100 overflow-auto campaign-details__wrap wrap integration-wrp">
      <img
        class="d-block mb-16 logo"
        alt="Facebook"
        src="/images/integrations/facebook.svg"
      />
      <ui-info class="mb-8">
        <a :href="`https://docs.bemob.com/${locale}/facebook-settings`" target="_blank">
          {{ t('Check our step-by-step guide to learn more.') }}
        </a>
      </ui-info>
      <div class="subtitle">1. {{ t('Facebook Details') }}</div>
      <ui-input
        v-model="form.name"
        :label="t('Integration Name') + '*'"
        :placeholder="t('Custom Name')"
        :error-messages="errorsDict['name']"
      />

      <ui-input
        v-model="form.clientId"
        :label="`${t('Client ID')} (${t('Pixel ID')})*`"
        :label-help="t('Access your Facebook Pixel\'s setting page → Details section → copy pixel ID')"
        :placeholder="t('Specify your Facebook Pixel ID')"
        :error-messages="errorsDict['clientId']"
      />

      <ui-input
        v-model="form.accessToken"
        :disabled="isEdit && !tokenEditMode"
        :class="{
          token__isEdit: isEdit,
          token__tokenEditMode: tokenEditMode,
        }"
        :label="t('API Key') + '*'"
        :label-help="t('Go to Facebook Pixel settings → scroll down to the Conversion API section →  click the Generate Access Token within Set up manually section')"
        :placeholder="t('Enter your Facebook API Key')"
        :error-messages="errorsDict['accessToken']"
      >
        <template #append>
          <ui-button
            v-if="isEdit"
            @click="handleEditTokenMode"
            class="token-change-mode"
            :icon="tokenEditMode ? 'fas fa-times' : 'fas fa-pen'"
          />
        </template>
      </ui-input>

      <ui-input
        v-model="form.meta.eventSourceUrl"
        :label="`${t('Event Source URL')}*`"
        :label-help="t('Verify in Facebook any domain that you own and have control over.')"
        :placeholder="t('Add your domain URL verified on the side of Facebook, e.g. https://test.com')"
        :error-messages="errorsDict['meta.eventSourceUrl']"
      />
      <div class="subtitle">2. {{ t('Facebook Events') }}</div>
      <ui-info class="mb-8">
        <span
          v-html="
            t(
              'With Facebook integration selected the postback URL option is not available.<br>All the conversions passing will be done via API.<br>You will need to specify the default Facebook Event Type in the field below or click on Add Event if you have custom conversions configured on the side of BeMob.<br>Event Type field refers to the conversion name and should be specified exactly how it is named on the side of Facebook.',
            )
          "
        />
      </ui-info>
      <ui-input
        v-model="form.meta.facebookDefaultEventType"
        :placeholder="t('Facebook Event Type')"
        :label="t('Facebook Event Type')"
        :labelHelp="t('Specify the name of Facebook event that you wish to track and forward back to Facebook.')"
        :error-messages="errorsDict['meta.facebookDefaultEventType']"
      />

      <button type="button" class="add-facebook-event-type" @click="addFacebookEventType">
        {{ t('+ Add Event Type For Custom Conversions') }}
        <ui-tooltip-icon
          class="ml-4"
          preset="help"
          :text="t('Use this option to pass the data on custom conversions to your Facebook ad account.')"
        />
      </button>

      <div
        class="facebook-event-type__row"
        v-for="(item, index) in form.meta.facebookCustomEventTypes"
        :key="index"
      >
        <ui-select
          class="grow"
          item-value="id"
          :label="t('BeMob Custom Conversion')"
          :labelHelp="t('Select the conversion event previously configured on the side of BeMob')"
          :items="customEvents(index)"
          v-model="form.meta.facebookCustomEventTypes[index].customEventId"
          :error-messages="getEventTypesError({ index, field: 'customEventId' })"
          :hide-details="2"
        />
        <ui-input
          class="grow"
          v-model="form.meta.facebookCustomEventTypes[index].facebookEventType"
          :placeholder="t('Facebook Event Type')"
          :label="t('Facebook Event Type')"
          :labelHelp="t('Specify the name of Facebook event that matches the conversion event in the list on the left.')"
          :error-messages="getEventTypesError({ index, field: 'facebookEventType' })"
          :hide-details="2"
        />
        <ui-icon
          class="delete-facebook-event-type"
          size="12"
          :color="$colors.red"
          @click="deleteFacebookEventType(index)"
          icon="fas fa-trash-alt"
        />
      </div>
      <div class="subtitle">3. {{ t('Facebook Params') }}</div>
      <ui-info class="mb-8">
        <span
          v-html="
            t(
              'Facebook requires at least one of the following parameters to be forwarded back for some types of events (e.g. Purchase, Lead etc).<br>In the fields below you can specify any static value, dynamic macros, custom 1-10 to forward the details which were initially passed to BeMob via postback URL of affiliate network or conversion pixel.',
            )
          "
        />
      </ui-info>
      <ui-input
        v-model="form.meta.facebookParamEmailValue"
        :placeholder="t('Email')"
        :label="t('Email')"
        :error-messages="errorsDict['meta.facebookParamEmailValue']"
      />
      <ui-input
        v-model="form.meta.facebookParamPhoneValue"
        :placeholder="t('Phone Number')"
        :label="t('Phone Number')"
        :error-messages="errorsDict['meta.facebookParamPhoneValue']"
      />
      <ui-input
        v-model="form.meta.facebookParamGenderValue"
        :placeholder="t('Gender')"
        :label="t('Gender')"
        :error-messages="errorsDict['meta.facebookParamGenderValue']"
      />
      <ui-input
        v-model="form.meta.facebookParamFirstNameValue"
        :placeholder="t('First Name')"
        :label="t('First Name')"
        :error-messages="errorsDict['meta.facebookParamFirstNameValue']"
      />
      <ui-input
        v-model="form.meta.facebookParamLastNameValue"
        :placeholder="t('Last Name')"
        :label="t('Last Name')"
        :error-messages="errorsDict['meta.facebookParamLastNameValue']"
      />
      <ui-input
        v-model="form.meta.facebookParamZipCodeValue"
        :placeholder="t('Zip Code')"
        :label="t('Zip Code')"
        :error-messages="errorsDict['meta.facebookParamZipCodeValue']"
      />
      <div class="subtitle">4. {{ t('Facebook Conversion Currency') }}</div>
      <ui-info class="mb-8">
        <span
          v-html="
            t(
              'by default, conversion values are sent in your bemob account currency.<br><br><b>example:</b>bemob currency is canadian dollar (cad).<br>facebook receives conversion in canadian dollar (cad).<br>select another preferred currency to override this behavior.',
            )
          "
        />
      </ui-info>
      <ui-select
        v-model="form.meta.currency"
        :placeholder="t('Currency')"
        :label="t('Currency')"
        :items="currencyListOrderedPreferred"
        item-text="name"
        item-value="code"
        :return-object="false"
        :hide-details="2"
        :menu-props="{ maxHeight: 190 }"
      />
    </div>

    <template #footer>
      <section
        class="justify-space-between align-center xs12 error-wrapper"
        style="display: flex; width: 100%"
      >
        <error-aggregator v-model="errors" v-model:active="errorsControlActive" />
        <div class="d-flex justify-end">
          <ui-update-info infoFormat="short" v-if="form.id" :data="props.data" />
          <ui-button
            :text="t('Cancel')"
            icon="fas fa-times"
            :disabled="loading"
            @click="close"
          />

          <ui-button
            :text="isEdit ? t('Save') : t('Create Integration')"
            icon="fas fa-check"
            button-style="accent"
            :loading="loading"
            @click="save"
          />
        </div>
      </section>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue';
import {
  postIntegrationApiRequest,
  putIntegrationApiRequest,
} from '@/services/api/integrations-api';
import { useApiFieldErrors, useColors, useCurrencyList, useI18n } from '@/composables';
import { useStore } from '@/store';
import { $emitter } from '@/services/emitter';

type FacebookCustomEventType = {
  customEventId: string;
  facebookEventType: string;
};

type FacebookIntegrationForm = {
  name: string;
  accessToken: string;
  type: string;
  id?: string;
  clientId?: string;
  meta: {
    facebookDefaultEventType: string;
    facebookCustomEventTypes: FacebookCustomEventType[];
    currency: string;
    eventSourceUrl: string;
    facebookParamEmailValue: string;
    facebookParamPhoneValue: string;
    facebookParamGenderValue: string;
    facebookParamFirstNameValue: string;
    facebookParamLastNameValue: string;
    facebookParamZipCodeValue: string;
  };
};

const props = defineProps<{
  data?: Partial<FacebookIntegrationForm> & { type?: string; meta?: Partial<FacebookIntegrationForm['meta']> };
}>();

const emit = defineEmits<{ close: [] }>();

const store = useStore();
const { t, locale } = useI18n();
const $colors = useColors();
const { currencyListOrderedPreferred } = useCurrencyList();
const { errors, errorsDict, errorsControlActive, clearErrors, applyApiErrors } =
  useApiFieldErrors();

const show = ref(false);
const loading = ref(false);
const isEdit = ref(false);
const tokenEditMode = ref(false);
const savedToken = ref('');
const errorsRaw = ref<Record<string, unknown>>({});

const getDefaultMeta = (): FacebookIntegrationForm['meta'] => ({
  facebookDefaultEventType: '',
  facebookCustomEventTypes: [],
  currency: store.user.currencyId,
  eventSourceUrl: '',
  facebookParamEmailValue: '',
  facebookParamPhoneValue: '',
  facebookParamGenderValue: '',
  facebookParamFirstNameValue: '',
  facebookParamLastNameValue: '',
  facebookParamZipCodeValue: '',
});

const getDefaultForm = (): FacebookIntegrationForm => ({
  name: '',
  accessToken: '',
  type: '',
  id: '',
  meta: getDefaultMeta(),
});

const form = reactive<FacebookIntegrationForm>(getDefaultForm());

const dataToSend = computed(() => {
  const { name, accessToken, id, type, clientId = '', meta } = form;
  return {
    ...(isEdit.value && { id }),
    ...(((isEdit.value && tokenEditMode.value) || !isEdit.value) && { accessToken }),
    name,
    clientId,
    type,
    meta,
  };
});

const customEvents = (index: number) => {
  const allCustomEvents = Object.values(store.customEvents) as customEvent[];
  return allCustomEvents
    .filter((item: customEvent) => {
      return (
        form.meta.facebookCustomEventTypes[index]?.customEventId === item.id ||
        (item.status !== 'archived' &&
          item.sendPostback &&
          !form.meta.facebookCustomEventTypes.find(
            row => row.customEventId === item.id,
          ))
      );
    })
    .map(item => ({
      ...item,
      text:
        item.status === 'archived'
          ? `${item.name} (${t('Archived').toLowerCase()})`
          : item.name,
    }));
};

const addFacebookEventType = () => {
  const newValue = [...form.meta.facebookCustomEventTypes];
  newValue.push({
    customEventId: '',
    facebookEventType: '',
  });
  form.meta.facebookCustomEventTypes = newValue;
};

const deleteFacebookEventType = (index: number) => {
  form.meta.facebookCustomEventTypes = form.meta.facebookCustomEventTypes.filter(
    (_, itemIndex) => itemIndex !== index,
  );
};

const getEventTypesError = ({
  index,
  field,
}: {
  index: number;
  field: string;
}) => {
  const meta = errorsRaw.value?.meta as Record<string, unknown> | undefined;
  const eventTypes = meta?.facebookCustomEventTypes as
    | Record<string, string>[]
    | undefined;
  const err = eventTypes?.[index]?.[field];
  if (err) {
    return [t(err, [t(field)])];
  }
};

const close = () => {
  emit('close');
};

const save = async () => {
  clearErrors();
  errorsRaw.value = {};
  loading.value = true;
  try {
    if (isEdit.value) {
      await putIntegrationApiRequest(dataToSend.value);
    } else {
      await postIntegrationApiRequest(dataToSend.value);
    }
    close();
    $emitter.emit('toast-success', t('Integration has been saved'));
    $emitter.emit('integrations-changed');
  } catch (error: unknown) {
    const axiosError = error as {
      response?: { data?: { errors?: Record<string, unknown> } };
    };
    errorsRaw.value = axiosError.response?.data?.errors || {};
    applyApiErrors(error, { nested: true });
  } finally {
    loading.value = false;
  }
};

const handleEditTokenMode = () => {
  if (tokenEditMode.value) {
    form.accessToken = savedToken.value;
  } else {
    form.accessToken = '';
  }
  tokenEditMode.value = !tokenEditMode.value;
};

onMounted(() => {
  Object.assign(form, getDefaultForm(), props.data, {
    meta: {
      ...getDefaultMeta(),
      ...props.data?.meta,
    },
  });

  tokenEditMode.value = false;
  if (props.data?.id) {
    isEdit.value = true;
    savedToken.value = props.data.accessToken || '';
  }

  show.value = true;
});
</script>

<style lang="scss" scoped>
.subtitle {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 8px;
}

.token {
  &__isEdit {
    :deep(input) {
      padding-right: 30px !important;
    }

    :deep(.v-input__append) {
      margin-inline-start: 8px;
    }

    :deep(.v-input--disabled .v-input__append) {
      pointer-events: auto;
    }
  }
}

.token-change-mode {
  margin: 0;
  background: none !important;
  border: none !important;

  :deep(.ui-button) {
    justify-content: center;
  }

  :deep(.ui-button__main) {
    justify-content: center;
    flex: 0 0 auto;
  }
}

.logo {
  height: 40px;
}

.integration-wrp {
  overflow-x: hidden !important;
}

.add-facebook-event-type {
  flex-grow: 1;
  text-align: center;
  padding: 0 10px 12px 0;
  color: var(--color-green);
  font-size: 13px;
  &:disabled {
    color: var(--color-gray5);
  }

  & + & {
    border-left: 2px solid var(--color-gray10);
  }
}

.facebook-event-type__row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  column-gap: 12px;
}

.delete-facebook-event-type {
  margin-top: 34px;
  cursor: pointer;
}
</style>
